import React from 'react';

const HOURS_12 = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const MINUTES_5 = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

function pad2(n) {
  return String(n).padStart(2, '0');
}

export function splitTime(hhmm) {
  const [hRaw, mRaw] = String(hhmm || '10:00').split(':');
  let h = Number(hRaw);
  let m = Number(mRaw);
  if (!Number.isFinite(h)) h = 10;
  if (!Number.isFinite(m)) m = 0;
  h = ((h % 24) + 24) % 24;
  m = Math.min(55, Math.max(0, Math.round(m / 5) * 5));
  return {
    hour12: h % 12 === 0 ? 12 : h % 12,
    minute: m,
    ampm: h >= 12 ? 'PM' : 'AM',
  };
}

export function joinTime(hour12, minute, ampm) {
  let h = Number(hour12) % 12;
  if (ampm === 'PM') h += 12;
  const m = Math.min(55, Math.max(0, Math.round(Number(minute) / 5) * 5));
  return `${pad2(h)}:${pad2(m)}`;
}

export function formatTime12(hhmm) {
  const { hour12, minute, ampm } = splitTime(hhmm);
  return `${hour12}:${pad2(minute)} ${ampm}`;
}

export function hhmmToMinutes(hhmm) {
  const [h, m] = String(hhmm || '00:00').split(':').map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
}

const selectStyle = {
  width: '100%',
  background: 'var(--bg-card)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '10px 8px',
  fontSize: 15,
  fontWeight: 700,
  cursor: 'pointer',
};

const labelStyle = {
  fontSize: 10,
  fontWeight: 700,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginBottom: 6,
  display: 'block',
};

export default function TimeDropdowns({ value, onChange, disabled = false, minHhmm = null }) {
  const { hour12, minute, ampm } = splitTime(value);
  const minMinutes = minHhmm ? hhmmToMinutes(minHhmm) : null;
  const optionPast = (h12, m, ap) => (
    minMinutes != null && hhmmToMinutes(joinTime(h12, m, ap)) < minMinutes
  );
  const hourDisabled = (h) => MINUTES_5.every((m) => optionPast(h, m, ampm));
  const ampmDisabled = (ap) => HOURS_12.every((h) => MINUTES_5.every((m) => optionPast(h, m, ap)));

  return (
    <div className="r-time-row" style={{ display: 'grid', gridTemplateColumns: '1.1fr 1.1fr 1fr', gap: 8 }}>
      <label>
        <span style={labelStyle}>Hour</span>
        <select
          disabled={disabled}
          value={hour12}
          onChange={(e) => onChange(joinTime(Number(e.target.value), minute, ampm))}
          style={{ ...selectStyle, cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          {HOURS_12.map((h) => (
            <option key={h} value={h} disabled={hourDisabled(h)}>{pad2(h)}</option>
          ))}
        </select>
      </label>
      <label>
        <span style={labelStyle}>Min</span>
        <select
          disabled={disabled}
          value={minute}
          onChange={(e) => onChange(joinTime(hour12, Number(e.target.value), ampm))}
          style={{ ...selectStyle, cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          {MINUTES_5.map((m) => (
            <option key={m} value={m} disabled={optionPast(hour12, m, ampm)}>{pad2(m)}</option>
          ))}
        </select>
      </label>
      <label>
        <span style={labelStyle}>AM / PM</span>
        <select
          disabled={disabled}
          value={ampm}
          onChange={(e) => onChange(joinTime(hour12, minute, e.target.value))}
          style={{ ...selectStyle, cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          <option value="AM" disabled={ampmDisabled('AM')}>AM</option>
          <option value="PM" disabled={ampmDisabled('PM')}>PM</option>
        </select>
      </label>
    </div>
  );
}
